import { useEffect, useRef } from 'react'
import {
  trackParticipantListViewed,
  trackPriorityFilterToggled,
  trackRoleFilterChanged,
  trackTagsFilterChanged,
} from 'src/mixpanel'
import { setParticipantListSnapshot } from 'src/mixpanel/participantListContext'
import { Filters } from './ParticipantListFilters'
import {
  PrioritySelectionChange,
  RoleSelectionChange,
  TagFilterAction,
  buildParticipantListSnapshot,
  buildParticipantListViewedProps,
  buildPriorityFilterToggledProps,
  buildRoleFilterChangedProps,
  buildTagsFilterChangedProps,
  countCaseload,
} from './participantListAnalytics'
import { PriorityFilter, PriorityFilterCounts } from './priorityFilterUtils'
import { RoleFilter } from './roleFilterUtils'
import { HomeViewParticipantFragment } from './searchParticipants.generated'

export type ParticipantListAnalyticsArgs = {
  /** The new triage UI is shown and its data has loaded. Nothing is tracked otherwise. */
  enabled: boolean
  /**
   * The new triage UI is shown and the query has data — true across a refetch, so the events
   * that read the snapshot keep their properties while the list reloads.
   */
  publishSnapshot: boolean
  filters: Filters
  allParticipants: ReadonlyArray<HomeViewParticipantFragment>
  filteredParticipants: ReadonlyArray<HomeViewParticipantFragment>
  uidsWithDraftMessage: Set<string>
  priorityCounts: PriorityFilterCounts
  selectedParticipant: HomeViewParticipantFragment | undefined
  selectedHasDraftMessage: boolean
}

/**
 * The participant list's Mixpanel tracking: `participant_list_viewed`, the three filter events,
 * and the triage snapshot that `chat_send`, `mark_as_unread` and `participant_opened` read.
 */
const useParticipantListAnalytics = ({
  enabled,
  publishSnapshot,
  filters,
  allParticipants,
  filteredParticipants,
  uidsWithDraftMessage,
  priorityCounts,
  selectedParticipant,
  selectedHasDraftMessage,
}: ParticipantListAnalyticsArgs) => {
  const filterState = { priorities: filters.priorities, tagCount: filters.tags.length, roles: filters.roles }

  useEffect(() => {
    setParticipantListSnapshot(
      publishSnapshot
        ? buildParticipantListSnapshot({
            filters: filterState,
            selectedParticipant,
            hasDraftMessage: selectedHasDraftMessage,
          })
        : null
    )
    return () => setParticipantListSnapshot(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishSnapshot, filters, selectedParticipant, selectedHasDraftMessage])

  // Keyed on the filter selection only, so the event fires when the list is first shown
  // and on every filter change — but never on a plain re-render. The last key is remembered
  // so that the list becoming ready again with the same filters doesn't re-fire it.
  const viewedKey = enabled ? JSON.stringify([filters.priorities, filters.tags, filters.roles]) : null
  const lastViewedKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (viewedKey !== null && viewedKey !== lastViewedKeyRef.current) {
      lastViewedKeyRef.current = viewedKey
      trackParticipantListViewed(
        buildParticipantListViewedProps({
          filters: filterState,
          resultCount: filteredParticipants.length,
          caseloadCount: countCaseload(allParticipants, uidsWithDraftMessage),
          priorityCounts,
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewedKey])

  // The pills are clickable while the list is still loading; a toggle then would report
  // counts over an empty list on a view that was never tracked.

  /** Call with the selection *after* the click. */
  const trackPrioritiesChanged = (priorities: Array<PriorityFilter>, change: PrioritySelectionChange) => {
    if (enabled) {
      trackPriorityFilterToggled(
        buildPriorityFilterToggledProps({
          change,
          priorities,
          tagCount: filters.tags.length,
          roles: filters.roles,
          priorityCounts,
        })
      )
    }
  }

  /**
   * Call with the tags *after* the change. Pass `roles` when they change in the same click
   * (Clear), since `filters` still holds the selection from before it.
   */
  const trackTagsChanged = (action: TagFilterAction, tags: Array<string>, roles: Array<RoleFilter> = filters.roles) => {
    if (enabled) {
      trackTagsFilterChanged(
        buildTagsFilterChangedProps({
          action,
          filters: { priorities: filters.priorities, tagCount: tags.length, roles },
        })
      )
    }
  }

  /** Call with the roles *after* the change; `tags` as for `trackTagsChanged`'s `roles`. */
  const trackRolesChanged = (
    roles: Array<RoleFilter>,
    change: RoleSelectionChange,
    tags: Array<string> = filters.tags
  ) => {
    if (enabled) {
      trackRoleFilterChanged(
        buildRoleFilterChangedProps({
          change,
          filters: { priorities: filters.priorities, tagCount: tags.length, roles },
        })
      )
    }
  }

  return { trackPrioritiesChanged, trackRolesChanged, trackTagsChanged }
}

export default useParticipantListAnalytics
